import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, useMediaQuery } from '@mui/material';
import Markdown from '@/components/Record/TalkFLow/Markdown';
import { FileType, ViewerSettings } from '@/types/fileViewer';

interface TextFileViewerProps {
  fileUrl: string;
  fileName: string;
  fileType: FileType;
  settings: ViewerSettings;
  containerRef?: React.RefObject<HTMLDivElement | null>;
  onError?: () => void;
}

// 텍스트 인코딩 감지 및 디코딩 (UTF-8 우선, 깨질 경우 EUC-KR 폴백)
const decodeText = (arrayBuffer: ArrayBuffer): string => {
  // 먼저 UTF-8로 시도
  const utf8Text = new TextDecoder('utf-8').decode(arrayBuffer);

  // 한글 정상 여부 및 깨짐(replacement character) 체크
  const hasValidKorean = /[가-힣]/.test(utf8Text);
  const hasGarbage = /[�]/.test(utf8Text);

  if (hasValidKorean && !hasGarbage) {
    return utf8Text;
  }

  // UTF-8 깨짐이 감지되면 EUC-KR (CP949) 시도
  if (hasGarbage) {
    try {
      const eucKrText = new TextDecoder('euc-kr').decode(arrayBuffer);
      if (/[가-힣]/.test(eucKrText)) {
        return eucKrText;
      }
    } catch {
      // EUC-KR 디코딩 실패시 UTF-8 반환
    }
  }

  return utf8Text;
};

const TextFileViewer: React.FC<TextFileViewerProps> = ({
  fileUrl,
  fileType,
  settings,
  containerRef,
  onError,
}) => {
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isMobile = useMediaQuery('(max-width:768px)');

  const { zoom } = settings;

  useEffect(() => {
    let isActive = true;

    const loadTextFile = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(fileUrl);
        if (!response.ok) {
          throw new Error(`Failed to fetch file: ${response.status}`);
        }
        const arrayBuffer = await response.arrayBuffer();
        const text = decodeText(arrayBuffer);
        if (isActive) {
          setContent(text);
        }
      } catch (err) {
        if (isActive) {
          setError('파일을 불러올 수 없습니다.');
          onError?.();
        }
        console.error(err);
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    loadTextFile();

    return () => {
      isActive = false;
    };
  }, [fileUrl, onError]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
        }}
      >
        <CircularProgress sx={{ color: '#FFFFFF' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          color: '#FFFFFF',
          padding: '40px',
          textAlign: 'center',
        }}
      >
        {error}
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        height: '100%',
        overflow: 'auto',
      }}
    >
      <Box
        sx={{
          // 모바일: 큰 외곽 여백(특히 상단) 제거하고 콘텐츠를 화면 폭에 맞춘다.
          padding: isMobile ? 0 : '40px',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            width: isMobile ? '100%' : `${800 * (zoom / 100)}px`,
            backgroundColor: '#FFFFFF',
            boxShadow: isMobile ? 'none' : '0px 2px 8px rgba(0, 0, 0, 0.1)',
            padding: isMobile ? '20px 16px' : `${40 * (zoom / 100)}px`,
            boxSizing: 'border-box',
            transformOrigin: 'top center',
            transition: 'width 0.2s ease-out, padding 0.2s ease-out',
            fontSize: `${zoom}%`,
            color: '#1A1A1A',
          }}
        >
          {fileType === 'markdown' ? (
            <Markdown>{content}</Markdown>
          ) : (
            <Box
              component="pre"
              sx={{
                margin: 0,
                fontFamily:
                  "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace",
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                lineHeight: 1.6,
              }}
            >
              {content}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default TextFileViewer;
